<template>
  <div class="donut-chart">
    <div v-if="loading" class="donut-chart__skeleton">
      <v-skeleton variant="circle" width="200px" height="200px" />
      <div class="donut-chart__skeleton-legend">
        <v-skeleton v-for="n in 6" :key="`legend-skeleton-${n}`" width="120px" height="12px" />
      </div>
    </div>
    <Doughnut
      v-else-if="hasData"
      :chart-id="chartId"
      :chart-data="chartData"
      :chart-options="chartOptions"
    />
    <div class="empty-state" v-else>
      <p class="empty-state__label">{{ emptyLabel }}</p>
      <empty-state>
        <template #badge><img src="@/assets/no-reports.svg" alt="" /></template>
      </empty-state>
    </div>
  </div>
</template>

<script>
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import VSkeleton from '@/components/v3/elements/VSkeleton';
import EmptyState from '@/views/common/empty-state';

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORICAL_COLORS = [
  '#254CB8',
  '#3A64DB',
  '#4D7CFE',
  '#98B3FF',
  '#E2EAFF',
  '#495063',
  '#6C7995',
  '#E1E5ED',
  '#ECEEF5',
  '#F5F6FA',
];

export default {
  name: 'VDonutChart',
  components: { Doughnut, VSkeleton, EmptyState },
  props: {
    chartId: {
      type: String,
      default: 'donut-chart',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    values: {
      type: Array,
      default: () => [],
    },
    colors: {
      type: Array,
      default: () => CATEGORICAL_COLORS,
    },
    valueFormatter: {
      type: Function,
      default: value => value,
    },
    emptyLabel: {
      type: String,
      default: 'No data to show',
    },
  },
  computed: {
    hasData() {
      return this.values.some(value => value > 0);
    },
    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            data: this.values,
            backgroundColor: this.colors.slice(0, this.values.length),
            borderColor: '#ffffff',
            borderWidth: 2,
            hoverOffset: 6,
          },
        ],
      };
    },
    chartOptions() {
      const format = this.valueFormatter;
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: {
          legend: {
            position: 'right',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              boxWidth: 8,
              padding: 14,
              color: '#495063',
              font: { size: 12 },
            },
          },
          tooltip: {
            callbacks: {
              label(context) {
                const total = context.dataset.data.reduce((sum, value) => sum + value, 0);
                const percent = total ? ((context.parsed / total) * 100).toFixed(1) : 0;
                return ` ${context.label}: ${format(context.parsed)} (${percent}%)`;
              },
            },
          },
        },
      };
    },
  },
};
</script>

<style scoped lang="scss">
.donut-chart {
  position: relative;
  width: 100%;
  height: 280px;

  &__skeleton {
    display: flex;
    align-items: center;
    gap: 32px;
    height: 100%;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;

    &__label {
      margin-top: 0;
      margin-bottom: 16px;
      color: $color-gray-6C;
      font-size: 14px;
      line-height: 22px;
      font-weight: 400;
      letter-spacing: 0.15px;
    }
  }

  &__skeleton-legend {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
}
</style>
